import { Pressable, ScrollView } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { Text, XStack, YStack } from 'tamagui';
import type { IconName } from '@kiips/ui';
import { Input, RowFocusContext, useRowFocusState, useThemeColors, Icon } from '@kiips/ui';
import { ResponsivePage } from './ResponsivePage';
import { CoverImage } from './CoverImage';
import { unsplashUrl } from '../../lib/unsplash';

const AUTH_HERO_IMAGE = unsplashUrl('photo-1488646953014-85cb44e25828', { w: 1000 });

/**
 * Shared layout + input primitives for the canonical auth screens
 * (/login, /signup, /forgot-password, /join/[linkId]). Extracted per
 * ADR-0028 — those screens previously each carried their own copy of
 * the hero, form card, FieldInput, and SubmitButton (≈85% identical).
 *
 * Components compose: <AuthScreenFrame><...form content...></AuthScreenFrame>.
 * All visual surfaces use Tamagui tokens so dark mode + rebrand work
 * (ADR-0029, ADR-0033). Touch targets are ≥44px (ADR-0026).
 */

interface AuthScreenFrameProps {
  /** Hero image URL — defaults to the shared travel photo. */
  image?: string | undefined;
  children: React.ReactNode;
}

export function AuthScreenFrame({ image, children }: AuthScreenFrameProps) {
  return (
    <ResponsivePage showBack>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 80 }}
        keyboardShouldPersistTaps="handled"
      >
        <YStack
          height={280}
          $md={{ height: 320 }}
          $lg={{ height: 360 }}
          backgroundColor="$textOnSurface"
          position="relative"
        >
          <CoverImage uri={image ?? AUTH_HERO_IMAGE} alt="" fill transition={300} />
        </YStack>

        <Animated.View entering={FadeIn.duration(280)}>
          <YStack
            marginTop={-32}
            marginHorizontal={16}
            $md={{ marginHorizontal: 'auto', width: '100%', maxWidth: 560 }}
            backgroundColor="$surface"
            borderRadius={28}
            padding={28}
            paddingTop={28}
          >
            {children}
          </YStack>
        </Animated.View>
      </ScrollView>
    </ResponsivePage>
  );
}

interface AuthFieldInputProps {
  placeholder: string;
  value: string;
  onChangeText: (s: string) => void;
  icon: IconName;
  keyboardType?: 'default' | 'email-address' | 'number-pad' | undefined;
  secureTextEntry?: boolean | undefined;
  autoComplete?: 'email' | 'current-password' | 'password' | 'new-password' | undefined;
  maxLength?: number | undefined;
}

export function AuthFieldInput({
  placeholder,
  value,
  onChangeText,
  icon,
  keyboardType,
  secureTextEntry,
  autoComplete,
  maxLength,
}: AuthFieldInputProps) {
  const theme = useThemeColors();
  const { focused, reporter } = useRowFocusState();
  return (
    <RowFocusContext.Provider value={reporter}>
      <XStack
        alignItems="center"
        gap={10}
        paddingHorizontal={16}
        height={52}
        backgroundColor="$surfaceCanvas"
        borderRadius={26}
        borderWidth={2}
        borderColor={focused ? '$accentCerulean' : 'transparent'}
      >
        <Icon name={icon} size={18} color={theme.textSubdued} />
        <Input
          variant="inline"
          placeholder={placeholder}
          value={value}
          onChangeText={onChangeText}
          keyboardType={keyboardType}
          secureTextEntry={secureTextEntry}
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          autoComplete={autoComplete}
          // Auth fields opt back in to autofill — credential inputs need
          // iOS's password / email suggestions.
          textContentType={
            secureTextEntry
              ? 'password'
              : keyboardType === 'email-address'
                ? 'emailAddress'
                : 'none'
          }
          importantForAutofill="yes"
          maxLength={maxLength}
          flex={1}
          fontSize={14}
        />
      </XStack>
    </RowFocusContext.Provider>
  );
}

interface AuthSubmitButtonProps {
  label: string;
  onPress: () => void;
  disabled?: boolean | undefined;
  accessibilityLabel?: string | undefined;
}

export function AuthSubmitButton({ label, onPress, disabled, accessibilityLabel }: AuthSubmitButtonProps) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      style={({ pressed }) => [
        {
          backgroundColor: theme.textOnSurface,
          height: 56,
          borderRadius: 28,
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 22,
        },
        disabled && { opacity: 0.6 },
        pressed && { transform: [{ scale: 0.98 }] },
      ]}
    >
      {/* Match the JS `theme.textOnSurface` button fill — a `$textInverse`
          CSS token resolves before the JS fill on web hydration, flashing
          a dark label on a still-dark button in dark mode. */}
      <Text fontSize={15} fontWeight="700" color={theme.textInverse}>{label}</Text>
    </Pressable>
  );
}

interface AuthFooterLinkProps {
  prefix: string;
  link: string;
  onPress: () => void;
  accessibilityLabel?: string | undefined;
}

export function AuthFooterLink({ prefix, link, onPress, accessibilityLabel }: AuthFooterLinkProps) {
  return (
    <XStack justifyContent="center" marginTop={22}>
      <Text fontSize={13} color="$textSubdued">{prefix}</Text>
      <Pressable onPress={onPress} accessibilityRole="link" accessibilityLabel={accessibilityLabel ?? link}>
        <Text fontSize={13} color="$textOnSurface" fontWeight="700"> {link}</Text>
      </Pressable>
    </XStack>
  );
}

interface AuthErrorMessageProps {
  message: string | null | undefined;
}

export function AuthErrorMessage({ message }: AuthErrorMessageProps) {
  if (!message) return null;
  return (
    <Text
      fontSize={13}
      color="$inkWarning"
      marginTop={12}
      textAlign="center"
      accessibilityRole="alert"
    >
      {message}
    </Text>
  );
}
